import { useEffect } from 'react';
import { useGameStore } from '../store/game-store.ts';
import { fetchGalaxyMap, fetchMe, fetchFleets, fetchResources, fetchCivilizations, fetchDiplomacy } from '../api/galaxy.ts';
import { installResourceSync } from '../economy/resource-sync.ts';
import { fetchActiveWork } from '../api/construction-overview.ts';
import { onApiMutation } from '../api/mutation-events.ts';
import { useWebSocket } from './useWebSocket.ts';

/** Loads the player's state once and keeps the live connection for every tab (not only the map). */
export function useGameBootstrap(): void {
  const { setGalaxyMap, setMe, setFleets, setResources, setLoading, setError, setKnownCivilizations, setDiplomacy } = useGameStore();
  useWebSocket(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [meData, map, fleetData] = await Promise.all([fetchMe(), fetchGalaxyMap(), fetchFleets()]);
        setMe(meData);
        setGalaxyMap(map);
        setFleets(fleetData.fleets);
        try { setResources(await fetchResources()); } catch { /* no resources yet */ }
        try { setKnownCivilizations(await fetchCivilizations()); } catch { /* ignore */ }
        try { setDiplomacy(await fetchDiplomacy()); } catch { /* ignore */ }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Betöltési hiba');
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [setGalaxyMap, setMe, setFleets, setResources, setLoading, setError, setKnownCivilizations, setDiplomacy]);

  // The server settles production on read; the display interpolates locally and resyncs on a timer, on focus /
  // visibility / reconnect and after every player action (economy/resource-sync)
  useEffect(() => installResourceSync(), []);
  useEffect(() => {
    let alive = true;
    let pending: ReturnType<typeof setTimeout> | null = null;
    const load = () => { void fetchActiveWork().then(r => { if (alive) useGameStore.getState().setActiveWork(r.items); }).catch(() => undefined); };
    const soon = () => { if (pending) clearTimeout(pending); pending = setTimeout(() => { pending = null; load(); }, 250); };
    const wake = () => { if (document.visibilityState === 'visible') load(); };
    load();
    const timer = setInterval(wake, 30_000);
    const off = onApiMutation(soon);
    window.addEventListener('focus', wake);
    window.addEventListener('galaxis:work-changed', soon);
    return () => { alive = false; clearInterval(timer); if (pending) clearTimeout(pending); off(); window.removeEventListener('focus', wake); window.removeEventListener('galaxis:work-changed', soon); };
  }, []);
}
